import { useState } from "react";
import JSZip from "jszip";
import { toast } from "sonner";
import { Instagram } from "lucide-react";
import { gbp, imgUrl, miles } from "@/lib/api";

const FOREST = "#16352C", CREAM = "#F7F4ED", STONE = "#DDD5C5";
const SERIF = '"Cormorant Garamond", Georgia, serif', SANS = "Manrope, sans-serif";
const S = 1080, PHOTO_H = 720;

const label = (status) => ({ sold: "Sold", reserved: "Reserved" }[status] || "Just arrived");

export const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

const tracked = (ctx, text, x, y, spacing, align = "left") => {
  const w = [...text].reduce((t, c) => t + ctx.measureText(c).width, 0) + spacing * (text.length - 1);
  let cx = align === "center" ? x - w / 2 : align === "right" ? x - w : x;
  for (const c of text) { ctx.fillText(c, cx, y); cx += ctx.measureText(c).width + spacing; }
  return w;
};

const drawCover = (ctx, img) => {
  const r = Math.max(S / img.width, PHOTO_H / img.height);
  const w = img.width * r, h = img.height * r;
  ctx.save();
  ctx.beginPath(); ctx.rect(0, 0, S, PHOTO_H); ctx.clip();
  ctx.drawImage(img, (S - w) / 2, (PHOTO_H - h) / 2, w, h);
  ctx.restore();
};

async function renderPost(v) {
  await Promise.all([`600 100px ${SERIF}`, `italic 400 40px ${SERIF}`, `700 30px ${SANS}`, `600 30px ${SANS}`].map((f) => document.fonts.load(f)));
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext("2d");
  const sold = v.status === "sold";

  ctx.fillStyle = FOREST; ctx.fillRect(0, 0, S, S);
  drawCover(ctx, await loadImage(imgUrl(v.images?.[0])));

  const badge = label(v.status).toUpperCase();
  ctx.font = `700 26px ${SANS}`; ctx.textBaseline = "middle";
  const bw = [...badge].reduce((t, c) => t + ctx.measureText(c).width, 0) + 6 * (badge.length - 1) + 64;
  ctx.fillStyle = sold ? CREAM : FOREST;
  ctx.fillRect(48, 48, bw, 64);
  ctx.fillStyle = sold ? FOREST : CREAM;
  tracked(ctx, badge, 48 + bw / 2, 81, 6, "center");

  ctx.textBaseline = "alphabetic";
  let priceW = 0;
  if (!sold && v.price) {
    ctx.fillStyle = CREAM; ctx.font = `600 76px ${SERIF}`; ctx.textAlign = "right";
    priceW = ctx.measureText(gbp(v.price)).width + 40;
    ctx.fillText(gbp(v.price), S - 60, 820);
    ctx.textAlign = "left";
  }
  const maxW = S - 120 - priceW;
  ctx.fillStyle = CREAM;
  ctx.font = `600 72px ${SERIF}`;
  ctx.fillText(`${v.year} ${v.make} ${v.model}`, 60, 820, maxW);
  ctx.fillStyle = STONE; ctx.font = `500 30px ${SANS}`;
  ctx.fillText(sold ? "Carefully selected. Properly prepared." : `${v.derivative || ""} · ${miles(v.mileage)}`, 60, 872, maxW);

  ctx.strokeStyle = STONE; ctx.globalAlpha = 0.5; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(60, 930); ctx.lineTo(S - 60, 930); ctx.stroke(); ctx.globalAlpha = 1;
  ctx.fillStyle = CREAM; ctx.font = `700 24px ${SANS}`;
  const ww = tracked(ctx, "CIRENCESTER", 60, 1000, 7);
  ctx.font = `600 52px ${SERIF}`; ctx.fillText("4×4", 60 + ww + 14, 1003);
  ctx.fillStyle = STONE; ctx.font = `italic 400 34px ${SERIF}`; ctx.textAlign = "right";
  ctx.fillText("cirencester4x4.co.uk", S - 60, 1000);
  return canvas;
}

const BAND = 120;
const LINE_MAX = S * (2 / 3);

const drawWordmark = (ctx, x, y) => {
  ctx.fillStyle = CREAM; ctx.font = `700 26px ${SANS}`; ctx.textBaseline = "alphabetic";
  const w = tracked(ctx, "CIRENCESTER", x, y, 7);
  ctx.font = `600 56px ${SERIF}`; ctx.fillText("4×4", x + w + 14, y + 3);
};

const drawContactLine = (ctx, x, y) => {
  const parts = ["www.cirencester4x4.co.uk", "hello@cirencester4x4.co.uk"];
  const gap = 28;
  let size = 28;
  const width = () => parts.reduce((t, p) => t + ctx.measureText(p).width, 0) + gap * 2;
  do { ctx.font = `500 ${size}px ${SANS}`; size -= 1; } while (x + width() > LINE_MAX && size > 14);
  ctx.fillStyle = CREAM; ctx.textBaseline = "middle";
  const w0 = ctx.measureText(parts[0]).width;
  ctx.fillText(parts[0], x, y);
  ctx.fillStyle = "#FFFFFF"; ctx.fillRect(x + w0 + gap - 1, y - 18, 2, 36);
  ctx.fillStyle = CREAM; ctx.fillText(parts[1], x + w0 + gap * 2, y);
  ctx.textBaseline = "alphabetic";
};

async function renderPhoto(src) {
  const img = await loadImage(imgUrl(src));
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = FOREST; ctx.fillRect(0, 0, S, S);
  const area = S - BAND * 2;
  const r = Math.min(S / img.width, area / img.height);
  const w = img.width * r, h = img.height * r;
  ctx.drawImage(img, (S - w) / 2, BAND + (area - h) / 2, w, h);
  drawWordmark(ctx, 60, BAND / 2 + 14);
  drawContactLine(ctx, 60, S - BAND / 2);
  return canvas;
}

export const toBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));

export function InstagramPostButton({ vehicle }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const zip = new JSZip();
      zip.file("01-post.jpg", await toBlob(await renderPost(vehicle)));
      const photos = vehicle.images || [];
      for (let i = 0; i < photos.length; i += 1) {
        zip.file(`${String(i + 2).padStart(2, "0")}-photo.jpg`, await toBlob(await renderPhoto(photos[i])));
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await zip.generateAsync({ type: "blob" }));
      a.download = `c4x4-instagram-${vehicle.slug}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success(`Downloaded post and ${photos.length} photos`);
    } catch {
      toast.error("Could not create the post. Check the vehicle has a main photo.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={download} disabled={busy} aria-label="Download Instagram post and photos" title="Download Instagram post and photos" data-testid={`instagram-post-${vehicle.slug}`} className="disabled:opacity-40">
      <Instagram className="h-4 w-4" />
    </button>
  );
}
